import { Injectable } from '@angular/core';
import { DateTimeService } from '@y42/date-time';
import { FeatureAggregationService, FeatureChildAggregationService } from '@y42/feature';
import { fullImportIcon, incrementalImportIcon } from '@y42/feature/icons';
import { Y42FeatureTypeToRoute, Y42RouterService } from '@y42/feature/routing';
import { ImportWriteType } from '@y42/json-schema';
import { JobStatusCode, JobStatusCodeToStatusMap, JobUIModel } from '../../state';
import { AverageSyncTimeService } from '../services/average-sync-time.service';
import { JobInfoUIModel } from './model';

@Injectable({ providedIn: 'root' })
export class JobInfoService {
  constructor(
    private readonly dateTimeService: DateTimeService,
    private readonly averageSyncTimeService: AverageSyncTimeService,
    private readonly featureChildAggregationService: FeatureChildAggregationService,
    private readonly featureAggregationService: FeatureAggregationService,
    private readonly y42RouterService: Y42RouterService,
  ) {}

  toJobInfoUIModel(job: JobUIModel): JobInfoUIModel {
    const isIncrementalImport = job?.details.write_disposition === ImportWriteType.Append;
    const isFullImport = job?.details.write_disposition === ImportWriteType.Truncate;
    const eventId = job ? this.getEventId(job) : undefined;
    let csvDownloadUrl;
    try {
      const externalUrl = job.details.external_url ? new URL(job.details.external_url) : undefined;
      csvDownloadUrl = externalUrl?.pathname.endsWith('.csv') ? job.details.external_url : undefined;
    } catch (error) {
      csvDownloadUrl = undefined;
    }

    const datasourceInfo = this.getDatasourceInfo(job);

    return {
      valid: JobStatusCodeToStatusMap[JobStatusCode.Ready].includes(job.status),
      invalid: JobStatusCodeToStatusMap[JobStatusCode.Failed].includes(job.status),
      queued: JobStatusCodeToStatusMap[JobStatusCode.Pending].includes(job.status),
      partial: !!job.details.partial,
      hasLogs: !!eventId,
      eventId,
      mode: isFullImport ? 'full' : isIncrementalImport ? 'incremental' : undefined,
      modeIcon: isFullImport ? fullImportIcon : isIncrementalImport ? incrementalImportIcon : undefined,
      syncTime: this.dateTimeService.formatDuration(this.averageSyncTimeService.getDifferenceInSeconds(job), {
        unit: 'seconds',
      }),
      importRowCount: job.details.current_import_row_count ?? 0,
      csvDownloadUrl,
      datasource: datasourceInfo,
      job: job,
    };
  }

  private getEventId(job: JobUIModel) {
    if (job.request.event_id) {
      return job.request.event_id;
    } else if (job.request.job_id) {
      return `${this.dateTimeService.__format(new Date(job.created_at), 'yyMMddHHmmss', true)}_${job.request.job_id}`;
    }
    return undefined;
  }

  private getDatasourceInfo(job: JobUIModel) {
    const parentPath = job.request.table_settings.datasource_table_path;
    const parentServices = parentPath ? this.featureChildAggregationService.getByGitPath(parentPath) : undefined;
    const parent = parentPath ? parentServices?.query.getUIEntity(parentPath) : undefined;

    const grandParentServices = parent ? this.featureAggregationService.getByGitPath(parent.$parent) : undefined;
    const grantParent = parent ? grandParentServices?.query.getUIEntity(parent.$parent) : undefined;

    return grantParent
      ? {
          name: grantParent.name,
          url: this.y42RouterService.getURLToDetail(Y42FeatureTypeToRoute[grantParent.featureType], grantParent.$name),
        }
      : undefined;
  }
}
